import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Search, PackageCheck, AlertCircle, Send, X, Sparkles, Download, ArrowLeft, ArrowRight, LogIn, FolderOpen, ChevronLeft, ChevronRight, FileText, DollarSign, Check as CheckIcon, Link2 as LinkIcon, Copy } from "lucide-react";
import { toast } from "sonner";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const dispBadge = (d) => {
  if (d === "agotado") return { label: "Agotado", cls: "bg-rose-100 text-rose-800 border-rose-200" };
  if (d === "bajo" || d === "pocas") return { label: "Bajo en stock", cls: "bg-amber-100 text-amber-800 border-amber-200" };
  return { label: "Disponible", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
};

function CategoriaCard({ categoria, previewProduct, onOpen, total }) {
  const nombre = categoria.nombre;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="text-left bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-primary/40 transition-all cursor-pointer group"
      data-testid={`cat-cat-${String(categoria.id).substring(0, 32)}`}
    >
      <div className="aspect-square bg-slate-100 relative overflow-hidden">
        {previewProduct?.tiene_imagen ? (
          <img
            src={`${backendUrl}/api/catalogo/img/${previewProduct.id}?w=400`}
            alt={nombre}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <FolderOpen className="w-14 h-14" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
        <span className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-primary/95 text-white font-bold uppercase tracking-wider backdrop-blur-sm">
          {total} {total === 1 ? "producto" : "productos"}
        </span>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-bold text-slate-800 line-clamp-2 min-h-[2.5rem] uppercase tracking-wide">
          {nombre}
        </h3>
        <div className="text-[11px] text-primary font-semibold mt-1.5 flex items-center gap-1">
          Ver colores <span className="group-hover:translate-x-1 transition-transform">→</span>
        </div>
      </div>
    </div>
  );
}

function ProductoVarianteCard({ producto, onOpen }) {
  const badge = dispBadge(producto.disponibilidad);
  const agotado = producto.disponibilidad === "agotado";
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`text-left rounded-xl border border-slate-200 overflow-hidden bg-white ${agotado ? "opacity-70" : "hover:shadow-md hover:border-primary/40"} transition-all cursor-pointer w-full`}
      data-testid={`cat-prod-${producto.id}`}
    >
      <div className="aspect-square bg-slate-100 relative">
        {producto.tiene_imagen ? (
          <img
            src={`${backendUrl}/api/catalogo/img/${producto.id}?w=400`}
            alt={producto.nombre}
            loading="lazy"
            decoding="async"
            className={`w-full h-full object-cover ${agotado ? "grayscale" : ""}`}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <PackageCheck className="w-12 h-12" />
          </div>
        )}
        <span className={`absolute top-2 right-2 text-[9px] px-2 py-0.5 rounded-full border font-bold uppercase ${badge.cls}`}>
          {badge.label}
        </span>
      </div>
      <div className="p-2.5">
        <h3 className={`text-xs font-semibold leading-tight line-clamp-2 min-h-[2rem] ${agotado ? "text-slate-500" : "text-slate-800"}`}>
          {producto.nombre}
        </h3>
        {producto.descripcion && (
          <p className="text-[10px] text-slate-500 leading-snug line-clamp-2 mt-0.5" title={producto.descripcion}>
            {producto.descripcion}
          </p>
        )}
        {producto.precio_display && (
          <div className="text-xs font-bold text-primary tabular-nums mt-1 truncate" data-testid={`cat-prod-precio-${producto.id}`}>
            {producto.precio_display}
          </div>
        )}
      </div>
    </button>
  );
}

// Feb/2026: Visor "profesional" del ítem — imagen grande, info, y navegación
// izquierda/derecha (flechas + teclado + swipe) por todos los items del contexto.
// El contexto puede ser un grupo (variantes de una familia) o el catálogo entero.
function ProductoDetalleModal({ items, indexInicial, onClose }) {
  const [idx, setIdx] = useState(indexInicial);
  const total = items.length;
  const p = items[idx];

  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

  // Teclado: flechas izquierda/derecha + ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  // Feb/2026: swipe táctil en móvil — deslizar izq/der sobre la imagen para
  // navegar entre productos, como una galería de fotos profesional.
  const touchRef = useRef({ startX: 0, startY: 0, active: false });
  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchRef.current = { startX: t.clientX, startY: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dy = t.clientY - touchRef.current.startY;
    touchRef.current.active = false;
    // Solo si es un swipe horizontal claro (>40px, y más horiz que vert)
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next(); else prev();
    }
  };

  if (!p) return null;
  const badge = dispBadge(p.disponibilidad);
  const agotado = p.disponibilidad === "agotado";

  return (
    <div
      className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="producto-detalle-modal"
    >
      {/* Flechas laterales */}
      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition"
            data-testid="detalle-prev"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition"
            data-testid="detalle-next"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}

      {/* Cerrar */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition z-10"
        data-testid="detalle-cerrar"
        aria-label="Cerrar"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Contador */}
      {total > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold">
          {idx + 1} / {total}
        </div>
      )}

      {/* Contenido */}
      <div
        className="max-w-4xl w-full max-h-[92vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Imagen grande — 60% en desktop, arriba en mobile */}
        <div className="relative bg-slate-100 md:w-3/5 aspect-square md:aspect-auto">
          {p.tiene_imagen ? (
            <img
              key={p.id}
              src={`${backendUrl}/api/catalogo/img/${p.id}?w=1000`}
              alt={p.nombre}
              className={`w-full h-full object-contain ${agotado ? "grayscale" : ""}`}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <PackageCheck className="w-24 h-24" />
            </div>
          )}
          <span className={`absolute top-3 left-3 text-[11px] px-2.5 py-1 rounded-full border font-bold uppercase ${badge.cls}`}>
            {badge.label}
          </span>
        </div>

        {/* Info */}
        <div className="md:w-2/5 p-5 md:p-6 overflow-y-auto flex flex-col">
          <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1">Producto</div>
          <h2 className="font-bold text-xl md:text-2xl text-slate-900 leading-tight mb-2">
            {p.nombre}
          </h2>
          {(p.codigo || p.sku) && (
            <div className="text-xs font-mono text-indigo-700 font-semibold mb-3">
              {p.codigo || p.sku}
            </div>
          )}
          {p.descripcion && (
            <div className="text-sm text-slate-600 leading-relaxed mb-4 whitespace-pre-wrap">
              {p.descripcion}
            </div>
          )}
          {p.unidad && (
            <div className="text-xs text-slate-500 mb-1">
              <span className="font-semibold uppercase text-[10px] tracking-wider">Unidad:</span> {p.unidad}
            </div>
          )}
          <div className="mt-auto pt-4 border-t text-[11px] text-slate-400">
            Usa las flechas ← → del teclado para navegar entre productos
          </div>
        </div>
      </div>
    </div>
  );
}

// Card de grupo público (feb/2026): muestra 1 tile por familia con badge de
// colores. Click abre modal con todos los colores.
function GrupoPublicoCard({ grupo, onOpen }) {
  const totalDisponible = grupo.items.filter((p) => p.disponibilidad !== "agotado").length;
  const preview = grupo.items.slice(0, 4);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="rounded-xl border-2 border-primary/30 bg-white text-left overflow-hidden hover:shadow-lg hover:border-primary/50 transition-all"
      data-testid={`cat-grupo-${(grupo.familia || "").substring(0, 20)}`}
    >
      <div className="aspect-square bg-slate-100 relative">
        {grupo.primero.tiene_imagen ? (
          <img
            src={`${backendUrl}/api/catalogo/img/${grupo.primero.id}?w=400`}
            alt={grupo.familia}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300">
            <PackageCheck className="w-12 h-12" />
          </div>
        )}
        <span className="absolute top-2 right-2 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
          {grupo.items.length} colores
        </span>
        {totalDisponible === 0 && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-600 text-white uppercase font-bold">Sin disponibilidad</span>
          </div>
        )}
      </div>
      <div className="p-2.5">
        <h3 className="text-xs font-semibold leading-tight line-clamp-2 min-h-[2rem] text-slate-800">
          {grupo.familia}
        </h3>
        <div className="flex gap-1 mt-1.5">
          {preview.map((p) => p.tiene_imagen ? (
            <div key={p.id} className="w-5 h-5 rounded overflow-hidden border border-slate-200">
              <img src={`${backendUrl}/api/catalogo/img/${p.id}?w=80`} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div key={p.id} className="w-5 h-5 rounded bg-slate-100 border border-slate-200" />
          ))}
          {grupo.items.length > 4 && (
            <span className="text-[10px] text-slate-400 self-center ml-0.5">+{grupo.items.length - 4}</span>
          )}
        </div>
        {/* Feb/2026: cuando el link incluye precios, mostramos el display del
            primer producto (el "representativo" de la familia). Todas las
            variantes de una familia comparten los mismos precios. */}
        {grupo.primero?.precio_display && (
          <div className="text-xs font-bold text-primary tabular-nums mt-1 truncate" data-testid={`cat-grupo-precio-${(grupo.familia || "").substring(0, 20)}`}>
            {grupo.primero.precio_display}
          </div>
        )}
      </div>
    </button>
  );
}

function GrupoPublicoModal({ grupo, onClose, onOpenItem }) {
  // Feb/2026: buscador dentro del grupo — filtra por nombre, color, código o SKU
  const [busqueda, setBusqueda] = useState("");
  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return grupo.items;
    const t = busqueda.trim().toLowerCase();
    return grupo.items.filter((p) => {
      const nombre = (p.nombre || "").toLowerCase();
      const color = (p.color || "").toLowerCase();
      const codigo = (p.codigo || "").toLowerCase();
      const sku = (p.sku || "").toLowerCase();
      return nombre.includes(t) || color.includes(t) || codigo.includes(t) || sku.includes(t);
    });
  }, [busqueda, grupo.items]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="grupo-publico-modal"
      >
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wider text-primary">Grupo</div>
              <h3 className="text-lg font-bold text-slate-900 truncate">{grupo.familia}</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {filtrados.length}
                {busqueda ? ` de ${grupo.items.length}` : ""} colores · haz click en cualquiera para ver detalle
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0"
              data-testid="grupo-publico-cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar en este grupo por color, código real o SKU..."
              className="w-full h-9 pl-9 pr-8 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 focus:outline-none"
              data-testid="grupo-buscar"
              autoFocus={false}
            />
            {busqueda && (
              <button
                onClick={() => setBusqueda("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400"
                data-testid="grupo-buscar-clear"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {filtrados.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              No se encontró ningún producto con "<span className="font-semibold text-slate-600">{busqueda}</span>" en este grupo.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtrados.map((p) => {
                // Buscar el índice REAL en el grupo original para que la
                // navegación en el detalle recorra todas las variantes.
                const idxReal = grupo.items.findIndex((x) => x.id === p.id);
                return (
                  <ProductoVarianteCard
                    key={p.id}
                    producto={p}
                    onOpen={() => onOpenItem(grupo.items, idxReal >= 0 ? idxReal : 0)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



export default function CatalogoPublicoPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("");
  const [orden, setOrden] = useState("recientes");
  const [modalInteres, setModalInteres] = useState(null); // producto
  const [grupoAbierto, setGrupoAbierto] = useState(null); // { familia, items }
  const [detalle, setDetalle] = useState(null); // { items, idx }
  const [exporting, setExporting] = useState(false);

  // Categorías preseleccionadas vía URL (?cats=id1,id2) por el vendedor
  const preselectedCats = (() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("cats");
    return v ? v.split(",").filter(Boolean) : [];
  })();
  // Feb/2026: settings de precios embebidos en el link personalizado del vendedor
  const linkPrecios = (() => {
    const params = new URLSearchParams(window.location.search);
    return {
      incluir_precios: params.get("incluir_precios") === "true",
      precio_modo: params.get("precio_modo") || "rango",
    };
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ orden });
      if (q) params.set("q", q);
      // Precedencia: dropdown del usuario > preselección de URL.
      // El backend usa 'cats' si viene; sino 'categoria'. Enviar ambos
      // hace que el filtro manual del cliente sea ignorado.
      if (categoria) {
        params.set("categoria", categoria);
      } else if (preselectedCats.length > 0) {
        params.set("cats", preselectedCats.join(","));
      }
      // Precios embebidos en el link (si el vendedor los activó)
      if (linkPrecios.incluir_precios) {
        params.set("incluir_precios", "true");
        params.set("precio_modo", linkPrecios.precio_modo);
      }
      const res = await axios.get(`${backendUrl}/api/catalogo/${slug}?${params.toString()}`);
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.status === 404 ? "Este catálogo no existe o el link es incorrecto." : "No se pudo cargar el catálogo.");
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, [slug, q, categoria, orden]);

  useEffect(() => { load(); }, [load]);

  const exportarJPG = useCallback(async () => {
    if (exporting) return;
    // Feb/2026: pre-check para evitar el falso "no se encontró item".
    // El bug era que a veces el backend recomputa los filtros y responde 404
    // aunque la grilla actual muestra items — mejor validamos localmente antes.
    if (!data || !Array.isArray(data.productos) || data.productos.length === 0) {
      toast.error("No hay productos visibles para exportar. Ajusta los filtros y vuelve a intentar.");
      return;
    }
    setExporting(true);
    const tid = toast.loading("Preparando catálogo…");
    // Feb/2026: retry automático una vez si el 1er intento devuelve 404
    // "no hay productos" — cubre races transitorios con Mongo.
    let attempt = 0;
    const maxAttempts = 2;
    try {
      const params = new URLSearchParams({ orden });
      if (q) params.set("q", q);
      if (categoria) {
        params.set("categoria", categoria);
      } else if (preselectedCats.length > 0) {
        params.set("cats", preselectedCats.join(","));
      }

      // 1) Inicia el job en background (con retry si el backend responde 404)
      let start;
      while (attempt < maxAttempts) {
        try {
          start = await axios.post(
            `${backendUrl}/api/catalogo/${slug}/exportar-jpg?${params.toString()}`,
            {},
            { timeout: 60000 },
          );
          break;
        } catch (err) {
          attempt += 1;
          const is404 = err?.response?.status === 404;
          if (is404 && attempt < maxAttempts) {
            // Espera breve y reintenta
            await new Promise((r) => setTimeout(r, 800));
            continue;
          }
          throw err;
        }
      }
      const jobId = start.data.job_id;
      const totalPag = start.data.total_paginas || 0;

      // 2) Polling cada 2s hasta done/error/timeout (5 min)
      const deadline = Date.now() + 5 * 60 * 1000;
      let status = "processing";
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        const st = await axios.get(
          `${backendUrl}/api/catalogo-export/${jobId}`,
          { timeout: 20000 },
        );
        status = st.data.status;
        const progress = st.data.progress || 0;
        if (status === "done") break;
        if (status === "error") throw new Error(st.data.error || "Error generando");
        toast.loading(
          totalPag ? `Generando página ${progress}/${totalPag}…` : "Generando…",
          { id: tid },
        );
      }
      if (status !== "done") throw new Error("Tiempo agotado. Intenta con menos productos.");

      // 3) Descargar cada página como imagen JPG separada (para que caiga en galería/descargas)
      toast.loading(`Descargando ${totalPag} imágenes…`, { id: tid });
      for (let i = 1; i <= totalPag; i++) {
        try {
          const res = await axios.get(
            `${backendUrl}/api/catalogo-export/${jobId}/page/${i}`,
            { responseType: "blob", timeout: 30000 },
          );
          const url = window.URL.createObjectURL(res.data);
          const link = document.createElement("a");
          link.href = url;
          const cd = res.headers["content-disposition"] || "";
          const match = cd.match(/filename="?([^"]+)"?/);
          link.download = match ? match[1] : `catalogo-beem-${String(i).padStart(2, "0")}.jpg`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => window.URL.revokeObjectURL(url), 2000);
          // Pequeña pausa para que el browser acepte la siguiente descarga
          await new Promise((r) => setTimeout(r, 350));
        } catch (err) {
          console.warn(`No se pudo descargar página ${i}`, err);
        }
      }
      // Liberar el job del backend
      axios.post(`${backendUrl}/api/catalogo-export/${jobId}/liberar`).catch(() => {});
      toast.success(`${totalPag} imágenes descargadas`, { id: tid });
    } catch (e) {
      const msg = e?.response?.status === 404
        ? "El servidor no encontró los productos para exportar. Recarga la página con F5 y vuelve a intentar."
        : (e?.message || "No se pudo generar el catálogo");
      toast.error(msg, { id: tid });
    } finally {
      setExporting(false);
    }
    // eslint-disable-next-line
  }, [slug, q, categoria, orden, exporting, data]);

  if (loading && !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-slate-500 animate-pulse">Cargando catálogo...</div>
    </div>
  );
  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md text-center">
        <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">Catálogo no encontrado</h1>
        <p className="text-sm text-slate-600">{err}</p>
      </div>
    </div>
  );
  if (!data) return null;

  // Preview y conteo por categoría — calculado desde los productos ya cargados
  // (sirve para la vista de categorías por defecto).
  const preview_por_categoria = new Map();
  for (const p of data.productos) {
    const cid = p.categoria_id || p.familia_key || "sin-categoria";
    if (!preview_por_categoria.has(cid)) {
      preview_por_categoria.set(cid, { count: 0, primero: null });
    }
    const g = preview_por_categoria.get(cid);
    g.count += 1;
    if (!g.primero || (p.tiene_imagen && !g.primero.tiene_imagen)) {
      g.primero = p;
    }
  }

  // Vista "categorías": cuando no hay filtro manual, no hay búsqueda y hay
  // suficientes categorías para valer la pena (>1). En caso contrario, mostrar
  // directamente los productos como colores.
  const mostrarCategorias = !categoria && !q && (data.categorias || []).length > 1;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="catalogo-publico">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{data.empresa.nombre}</span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 mt-0.5 truncate">Catálogo de {data.vendedor.nombre}</h1>
            <p className="text-xs text-slate-500 mt-0.5">Inventario actualizado en tiempo real · {data.total} productos</p>
          </div>
          {/* Oct/2026: el botón "Volver al sistema" solo tiene sentido para
              vendedores logeados que abren su propio catálogo (ej. desde la
              PWA en el celular, donde no hay botón "atrás" nativo). Los
              clientes finales NO deben verlo — ellos no tienen cuenta. */}
          {typeof window !== "undefined" && localStorage.getItem("atlas_token") && (
            <button
              type="button"
              onClick={() => { window.location.href = "/"; }}
              className="flex-shrink-0 h-10 px-3 md:px-4 rounded-full text-xs md:text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5 shadow-sm"
              data-testid="cat-volver-app"
              title="Volver al sistema"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Volver al sistema</span>
              <span className="sm:hidden">Sistema</span>
            </button>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col md:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto o SKU..."
                   className="w-full h-10 pl-10 pr-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
                   data-testid="cat-search" />
          </div>
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                  className="h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white md:w-56" data-testid="cat-categoria">
            <option value="">Todas las categorías</option>
            {data.categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
          <select value={orden} onChange={(e) => setOrden(e.target.value)}
                  className="h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white md:w-48" data-testid="cat-orden">
            <option value="recientes">Más recientes</option>
            <option value="stock">Más disponibles</option>
            <option value="nombre">Nombre A-Z</option>
          </select>
        </div>

        {/* Breadcrumb — si el usuario está dentro de una categoría */}
        {categoria && (
          <div className="mb-4 flex items-center justify-between gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <FolderOpen className="w-4 h-4 text-primary flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Categoría</div>
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {data.categorias.find((c) => c.id === categoria)?.nombre || categoria}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCategoria("")}
              className="flex-shrink-0 h-9 px-3 text-xs font-semibold rounded-full bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 flex items-center gap-1.5"
              data-testid="cat-volver-categorias"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Volver a categorías</span>
            </button>
          </div>
        )}

        {/* Grid principal */}
        {data.productos.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500" data-testid="cat-vacio">
            No hay productos que coincidan con la búsqueda.
          </div>
        ) : mostrarCategorias ? (
          <>
            <div className="mb-3 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                {data.categorias.length} categorías
              </div>
              <div className="text-xs text-slate-400">Toca una categoría para ver todos los colores</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cat-grid-categorias">
              {data.categorias.map((c) => {
                const info = preview_por_categoria.get(c.id) || { count: 0, primero: null };
                return (
                  <CategoriaCard
                    key={c.id}
                    categoria={c}
                    previewProduct={info.primero}
                    total={info.count}
                    onOpen={() => setCategoria(c.id)}
                  />
                );
              })}
            </div>
          </>
        ) : (() => {
          // Feb/2026: agrupar productos por familia_key en la vista pública
          // (el cliente ve grupos, no el "chorrero" de colores). Click sobre
          // el grupo abre un modal listando cada variante.
          const grupos = new Map();
          for (const p of data.productos) {
            const key = (p.familia_key || "").trim().toUpperCase();
            const nombre = String(p.nombre || "").trim();
            const idx = nombre.lastIndexOf(" - ");
            const familiaNombre = idx > 0 ? nombre.substring(0, idx).trim() : nombre;
            const groupingKey = key || familiaNombre.toUpperCase() || `SOLO_${p.id}`;
            if (!grupos.has(groupingKey)) {
              grupos.set(groupingKey, { familia: familiaNombre, items: [] });
            }
            grupos.get(groupingKey).items.push(p);
          }
          const listaGrupos = Array.from(grupos.values()).map((g) => ({
            ...g,
            primero: g.items.find((p) => p.tiene_imagen) || g.items[0],
          }));
          // Flatten para el visor: todas las variantes ordenadas como aparecen
          // en la grilla, para que las flechas del detalle recorran TODO el
          // catálogo (no solo el grupo actual).
          const flatFlow = listaGrupos.flatMap((g) => g.items);
          return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cat-grid-productos">
              {listaGrupos.map((g) => (
                g.items.length === 1 ? (
                  <ProductoVarianteCard
                    key={g.items[0].id}
                    producto={g.items[0]}
                    onOpen={() => {
                      const idx = flatFlow.findIndex((x) => x.id === g.items[0].id);
                      setDetalle({ items: flatFlow, idx: idx >= 0 ? idx : 0 });
                    }}
                  />
                ) : (
                  <GrupoPublicoCard
                    key={g.primero.id}
                    grupo={g}
                    onOpen={() => setGrupoAbierto(g)}
                  />
                )
              ))}
            </div>
          );
        })()}

        <footer className="mt-8 py-6 text-center text-xs text-slate-400">
          Para hacer pedidos, contacta a <b>{data.vendedor.nombre}</b>. · Powered by Beem Importaciones
        </footer>
      </div>

      {modalInteres && (
        <InteresModal
          producto={modalInteres}
          slug={slug}
          onClose={() => setModalInteres(null)}
        />
      )}
      {grupoAbierto && (
        <GrupoPublicoModal
          grupo={grupoAbierto}
          onClose={() => setGrupoAbierto(null)}
          onOpenItem={(items, i) => setDetalle({ items, idx: i })}
        />
      )}
      {detalle && (
        <ProductoDetalleModal
          items={detalle.items}
          indexInicial={detalle.idx}
          onClose={() => setDetalle(null)}
        />
      )}
    </div>
  );
}

function InteresModal({ producto, slug, onClose }) {
  const [form, setForm] = useState({ nombre: "", whatsapp: "", notas: "" });
  const [enviando, setEnviando] = useState(false);
  const [ok, setOk] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.whatsapp.trim()) return toast.error("Nombre y WhatsApp obligatorios");
    setEnviando(true);
    try {
      await axios.post(`${backendUrl}/api/catalogo/${slug}/interes`, {
        producto_id: producto.id,
        nombre: form.nombre.trim(),
        whatsapp: form.whatsapp.trim(),
        notas: form.notas.trim() || null,
      });
      setOk(true);
      setTimeout(onClose, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando");
    } finally { setEnviando(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="modal-interes">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Avísame cuando llegue</div>
            <div className="text-sm font-semibold text-slate-800 mt-0.5">{producto.nombre}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        {ok ? (
          <div className="p-8 text-center">
            <PackageCheck className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <div className="text-lg font-bold text-slate-800">¡Listo!</div>
            <div className="text-sm text-slate-500 mt-1">Te contactaremos cuando llegue este producto.</div>
          </div>
        ) : (
          <form onSubmit={enviar} className="p-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Tu nombre</label>
              <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                     className="w-full h-10 mt-1 px-3 border border-slate-200 rounded-lg text-sm" data-testid="int-nombre" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">WhatsApp</label>
              <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                     placeholder="+57 300..." className="w-full h-10 mt-1 px-3 border border-slate-200 rounded-lg text-sm" data-testid="int-whatsapp" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Nota (opcional)</label>
              <textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                        rows={2} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none" data-testid="int-notas" />
            </div>
            <button type="submit" disabled={enviando}
                    className="w-full h-11 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="int-send">
              <Send className="w-4 h-4" /> {enviando ? "Enviando..." : "Avisarme"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
